import React, { useEffect, useState, useMemo, useRef } from "react";
import { fetchBusRoutes, fetchBusStopsForRoute } from "../lib/api";
import { BUS_API_KEY } from "../lib/config";

// Each bus config entry: { busRouteId, busDirectionId, busStopId, label }
function BusConfigCard({ entry, index, allRoutes, loadingRoutes, onUpdate, onRemove }) {
    const [routeQuery, setRouteQuery] = useState("");
    const [routeOpen, setRouteOpen] = useState(false);
    const [directions, setDirections] = useState([]);
    const [loadingStops, setLoadingStops] = useState(false);
    const routeRef = useRef(null);

    useEffect(() => {
        function handleClick(e) {
            if (routeRef.current && !routeRef.current.contains(e.target)) setRouteOpen(false);
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    useEffect(() => {
        if (!entry.busRouteId) { setDirections([]); return; }
        setLoadingStops(true);
        fetchBusStopsForRoute(entry.busRouteId, BUS_API_KEY)
            .then(setDirections)
            .catch(() => setDirections([]))
            .finally(() => setLoadingStops(false));
    }, [entry.busRouteId]);

    const filteredRoutes = useMemo(() => {
        if (!routeQuery.trim()) return allRoutes.slice(0, 40);
        const q = routeQuery.toLowerCase();
        return allRoutes.filter(r =>
            r.shortName?.toLowerCase().includes(q) ||
            r.longName?.toLowerCase().includes(q)
        ).slice(0, 40);
    }, [allRoutes, routeQuery]);

    const selectedRoute = allRoutes.find(r => r.id === entry.busRouteId);
    const selectedDirection = directions.find(d => d.id === entry.busDirectionId);
    const stopsForDirection = selectedDirection?.stops || [];

    return (
        <div className="transfer-group-card">
            <div className="transfer-group-header">
                <div className="settings-field" style={{ flex: 1, marginBottom: 0 }}>
                    <label>🔍 Bus Route</label>
                    {loadingRoutes ? (
                        <p style={{ color: '#888', fontSize: '0.9rem' }}>Loading...</p>
                    ) : (
                        <div className="stop-picker" ref={routeRef}>
                            <input
                                type="text"
                                className="stop-picker-input"
                                placeholder={selectedRoute ? `${selectedRoute.shortName} – ${selectedRoute.longName}` : "Search bus routes..."}
                                value={routeOpen ? routeQuery : ""}
                                onFocus={() => setRouteOpen(true)}
                                onChange={(e) => { setRouteQuery(e.target.value); setRouteOpen(true); }}
                            />
                            {selectedRoute && !routeOpen && (
                                <div className="stop-picker-selected" onClick={() => setRouteOpen(true)}>
                                    🚌 <strong>{selectedRoute.shortName}</strong> – {selectedRoute.longName}
                                </div>
                            )}
                            {routeOpen && (
                                <ul className="stop-picker-dropdown">
                                    {filteredRoutes.length === 0 ? (
                                        <li className="stop-picker-empty">No routes found</li>
                                    ) : filteredRoutes.map(r => (
                                        <li key={r.id}
                                            className={`stop-picker-option ${r.id === entry.busRouteId ? 'selected' : ''}`}
                                            onClick={() => {
                                                onUpdate({ busRouteId: r.id, busDirectionId: "", busStopId: "", label: "" });
                                                setRouteQuery(""); setRouteOpen(false);
                                            }}>
                                            <span><strong style={{ marginRight: 8 }}>{r.shortName}</strong>{r.longName}</span>
                                            <span className="stop-picker-id">{r.description}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
                <button className="icon-btn danger" onClick={onRemove} title="Remove">✕</button>
            </div>

            {entry.busRouteId && (
                <>
                    <div className="settings-field" style={{ marginTop: 12 }}>
                        <label>📍 Direction</label>
                        {loadingStops ? (
                            <p style={{ color: '#888', fontSize: '0.9rem' }}>Loading...</p>
                        ) : (
                            <div className="bus-direction-grid">
                                {directions.map(d => (
                                    <button key={d.id}
                                        className={`bus-direction-btn ${entry.busDirectionId === d.id ? 'active' : ''}`}
                                        onClick={() => onUpdate({ ...entry, busDirectionId: d.id, busStopId: "" })}>
                                        {d.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {entry.busDirectionId && stopsForDirection.length > 0 && (
                        <div className="settings-field" style={{ marginTop: 8 }}>
                            <label>🚏 Bus Stop</label>
                            <select
                                value={entry.busStopId}
                                onChange={(e) => onUpdate({ ...entry, busStopId: e.target.value })}
                                style={{ width: '100%', padding: '10px 14px', fontSize: '0.95rem', borderRadius: 10, border: '2px solid #e5e7eb' }}>
                                <option value="">Select a stop</option>
                                {stopsForDirection.map(s => (
                                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {entry.busStopId && (
                        <div className="settings-field" style={{ marginTop: 8 }}>
                            <label>Label</label>
                            <input
                                type="text"
                                placeholder="e.g. To Flushing"
                                value={entry.label || ""}
                                onChange={(e) => onUpdate({ ...entry, label: e.target.value })}
                                className="settings-input-md"
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function BusConfigurator({ busConfigs, onChange }) {
    const [allRoutes, setAllRoutes] = useState([]);
    const [loadingRoutes, setLoadingRoutes] = useState(true);

    useEffect(() => {
        setLoadingRoutes(true);
        fetchBusRoutes(BUS_API_KEY)
            .then(routes => {
                routes.sort((a, b) => (a.shortName || "").localeCompare(b.shortName || "", undefined, { numeric: true }));
                setAllRoutes(routes);
            })
            .catch(() => setAllRoutes([]))
            .finally(() => setLoadingRoutes(false));
    }, []);

    function updateEntry(idx, newEntry) {
        const updated = busConfigs.map((e, i) => i === idx ? newEntry : e);
        onChange(updated);
    }

    function removeEntry(idx) {
        onChange(busConfigs.filter((_, i) => i !== idx));
    }

    function addEntry() {
        onChange([...busConfigs, { busRouteId: "", busDirectionId: "", busStopId: "", label: "" }]);
    }

    return (
        <div className="bus-configurator">
            {busConfigs.map((entry, idx) => (
                <BusConfigCard
                    key={idx}
                    entry={entry}
                    index={idx}
                    allRoutes={allRoutes}
                    loadingRoutes={loadingRoutes}
                    onUpdate={(e) => updateEntry(idx, e)}
                    onRemove={() => removeEntry(idx)}
                />
            ))}
            <button className="add-btn" onClick={addEntry}>+ Add Bus Route</button>
        </div>
    );
}

export default BusConfigurator;
